import { useSearchParams } from 'react-router-dom';
import { useCallback } from 'react';

const VALID_TABS = ['performance', 'research', 'recommendations', 'trades', 'setup'];
const DEFAULT_TAB = 'performance';

/**
 * URL-driven workspace state hook.
 * Reads/writes tab, modal, step, and chat state via URL search params
 * so that all workspace state is deep-linkable and bookmarkable.
 *
 * Replaces the Zustand activeTab numeric index.
 */
export function useWorkspaceParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = VALID_TABS.includes(searchParams.get('tab'))
    ? searchParams.get('tab')
    : DEFAULT_TAB;

  const setTab = useCallback((name) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (name === DEFAULT_TAB) next.delete('tab');
      else next.set('tab', name);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const modal = searchParams.get('modal');
  const step = searchParams.get('step');
  const chat = searchParams.get('chat');

  const setModal = useCallback((name, stepNum) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('modal', name);
      if (stepNum) next.set('step', String(stepNum));
      else next.delete('step');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const closeModal = useCallback(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('modal');
      next.delete('step');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  return { tab, setTab, modal, step, chat, setModal, closeModal };
}
